// client/src/components/profissionais/sections/ProfissionalProfileSection.js
import React, { useState, useEffect } from 'react';
import useProfissionais, { useProfissionalPorUsuario } from '../../../hooks/useProfissionais';
import { Loader2, Save, XCircle, AlertCircle, User, Briefcase, Settings } from 'lucide-react';
import useAlert from '../../../hooks/useAlert';

const ProfissionalProfileSection = ({ user, contextData, onUpdateSuccess }) => {
    const { setAlert } = useAlert();
    const profissionais = useProfissionais();
    const contaId = contextData?.contaId; 
    
    const { data: responseData, isLoading } = useProfissionalPorUsuario(user?._id, contaId, {
        enabled: !!user?._id && !!contaId
    });
    
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState({
        especialidades: [],
        registroProfissional: '',
        status: 'ativo',
        disponivelParaAgendamento: true,
        metadata: {
            descricaoCurta: '',
            biografia: ''
        }
    });

    const isUserInativo = user?.isActive === false;

    useEffect(() => {
        if (isLoading) return;
        
        const profData = responseData?.data || responseData;
        
        if (profData && (profData.id || profData._id)) {
            setFormData({
                especialidades: profData.especialidades || [], 
                registroProfissional: profData.registroProfissional || '',
                status: isUserInativo ? 'inativo' : (profData.status || 'ativo'),
                disponivelParaAgendamento: isUserInativo ? false : (profData.disponivelParaAgendamento !== false),
                metadata: {
                    descricaoCurta: profData.metadata?.descricaoCurta || '',
                    biografia: profData.metadata?.biografia || ''
                }
            });
            setIsCreating(false);
        } else {
            setIsCreating(true);
            setIsEditing(true);
            if(isUserInativo) {
                 setFormData(prev => ({...prev, status: 'inativo', disponivelParaAgendamento: false}));
            }
        }
    }, [responseData, isLoading, isUserInativo]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const finalValue = type === 'checkbox' ? checked : value;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: finalValue
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: finalValue }));
        }
    };

    const handleEspecialidadeChange = (index, value) => {
        const novasEspecialidades = [...formData.especialidades];
        novasEspecialidades[index] = value;
        setFormData(prev => ({ ...prev, especialidades: novasEspecialidades }));
    };

    const handleAddEspecialidade = () => {
        setFormData(prev => ({
            ...prev,
            especialidades: [...prev.especialidades, '']
        }));
    };

    const handleRemoveEspecialidade = (index) => {
        const novasEspecialidades = formData.especialidades.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, especialidades: novasEspecialidades }));
    };

    const handleSave = async () => {
        try {
            if (!user?._id) {
                setAlert('ID do usuário não encontrado', 'danger');
                return;
            }

            const dataToSave = {
                userId: user._id,
                accountId: contaId,
                ...formData,
                especialidades: formData.especialidades.filter(esp => esp.trim() !== '')
            };

            if (isCreating) {
                await profissionais.createProfissional.mutateAsync(dataToSave);
                setAlert('Perfil profissional criado com sucesso!', 'success');
            } else {
                const profData = responseData?.data || responseData;
                const profissionalId = profData.id || profData._id;
                await profissionais.updateProfissional.mutateAsync({
                    profissionalId,
                    data: dataToSave
                });
                setAlert('Dados profissionais atualizados com sucesso!', 'success');
            }
            
            setIsEditing(false);
            if (onUpdateSuccess) onUpdateSuccess();
        } catch (error) {
            setAlert(error.message || `Erro ao ${isCreating ? 'criar' : 'atualizar'} dados`, 'danger');
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (isCreating) window.location.reload();
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="text-center py-12">
                <AlertCircle size={48} className="mx-auto text-yellow-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Usuário não encontrado</h3>
            </div>
        );
    }

    return (
        <div className="form-content">
            {/* CABEÇALHO PADRONIZADO */}
            <div className="form-header flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <h3 className="form-title mb-0 flex items-center">
                        <User size={20} className="inline mr-2 text-primary"/>
                        Dados Profissionais
                    </h3>
                    {!isCreating && (
                        <span className={`ml-4 px-2 py-1 rounded-full text-xs font-medium ${
                            formData.status === 'ativo' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                        }`}>
                            {formData.status === 'ativo' ? 'Ativo' : 
                                formData.status === 'ferias' ? 'Férias' :
                                formData.status === 'licenca' ? 'Licença' : 'Inativo'}
                        </span>
                    )}
                </div>
                
                {!isCreating && !isEditing ? (
                    <button 
                        type="button" 
                        onClick={() => setIsEditing(true)} 
                        className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                    >
                        <Settings size={16} className="mr-2 text-gray-500" /> Editar Dados
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button 
                            type="button" 
                            onClick={handleCancel} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <XCircle size={16} className="mr-2 text-gray-500" /> Cancelar
                        </button>
                        <button 
                            type="button" 
                            onClick={handleSave} 
                            disabled={profissionais.isPending} 
                            className="action-button action-button-primary bg-primary text-white shadow-sm"
                        >
                            {profissionais.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                            Salvar Dados
                        </button>
                    </div>
                )}
                
                {/* Mantido apenas para compatibilidade legada se for acionado de fora */}
                <div style={{ display: 'none' }}>
                    <button id="trigger-save-profile" type="button" onClick={handleSave}></button>
                </div>
            </div>

            {isUserInativo && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-4 flex items-center text-sm">
                    <AlertCircle size={16} className="mr-2 shrink-0" />
                    Este usuário encontra-se desativado do sistema principal. O perfil profissional ficará inativo automaticamente até que ele seja reativado.
                </div>
            )}

            <div className="form-tabs-content p-0">
                <fieldset className="form-section" disabled={!isEditing || profissionais.isPending}>
                    <div className="form-grid-container">
                        <div className="form-group">
                            <label>Registro Profissional</label>
                            <input 
                                type="text" 
                                name="registroProfissional" 
                                value={formData.registroProfissional} 
                                onChange={handleChange} 
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`} 
                                disabled={!isEditing}
                                placeholder="Ex: CRM-12345"
                            />
                        </div>

                        <div className="form-group">
                            <label>Status</label>
                            <select 
                                name="status" 
                                value={formData.status} 
                                onChange={handleChange} 
                                className={`form-input ${(!isEditing || isUserInativo) ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                disabled={!isEditing || isUserInativo}
                            >
                                <option value="ativo">Ativo</option>
                                <option value="inativo">Inativo</option>
                                <option value="ferias">Férias</option>
                                <option value="licenca">Licença</option>
                            </select>
                        </div>

                        <div className="form-group md:col-span-2 mt-2">
                            <label className="checkbox-label font-medium text-gray-800">
                                <input 
                                    type="checkbox" 
                                    name="disponivelParaAgendamento" 
                                    checked={formData.disponivelParaAgendamento} 
                                    onChange={handleChange} 
                                    className="form-checkbox w-4 h-4"
                                    disabled={!isEditing || isUserInativo}
                                />
                                <span className="checkbox-label-text ml-2">O profissional atende agendamentos no momento</span>
                            </label>
                        </div>

                        <div className="form-group md:col-span-2 mt-4 pt-4 border-t border-gray-100">
                            <label className="font-medium text-gray-800 mb-2 block">Especialidades</label>
                            <div className="space-y-2">
                                {formData.especialidades.map((esp, index) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            type="text"
                                            value={esp}
                                            onChange={(e) => handleEspecialidadeChange(index, e.target.value)}
                                            className={`form-input flex-1 ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                            placeholder="Ex: Fisioterapia, Design de Sobrancelha"
                                            disabled={!isEditing}
                                        />
                                        {isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveEspecialidade(index)}
                                                className="action-button-icon text-red-600 hover:text-red-900 border-red-200"
                                            >
                                                <XCircle size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {isEditing && (
                                    <button
                                        type="button"
                                        onClick={handleAddEspecialidade}
                                        className="text-sm text-primary hover:underline font-medium flex items-center mt-2"
                                    >
                                        + Adicionar especialidade
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="form-group md:col-span-2 mt-4 pt-4 border-t border-gray-100">
                            <label>Descrição Curta</label>
                            <textarea
                                name="metadata.descricaoCurta"
                                value={formData.metadata.descricaoCurta}
                                onChange={handleChange}
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                rows="2"
                                placeholder="Breve descrição do profissional..."
                                disabled={!isEditing}
                            />
                        </div>

                        <div className="form-group md:col-span-2">
                            <label>Biografia Completa</label>
                            <textarea
                                name="metadata.biografia"
                                value={formData.metadata.biografia}
                                onChange={handleChange}
                                className={`form-input ${!isEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                rows="4"
                                placeholder="Biografia completa do profissional..."
                                disabled={!isEditing}
                            />
                        </div>
                    </div>
                </fieldset>
            </div>
        </div>
    );
};

export default ProfissionalProfileSection;